/**
 * Relationship Compass - Values & Priorities Alignment Assessment
 *
 * This assessment explores value alignment and priority compatibility between partners.
 * It uses ranking exercises and scenario-based questions to reveal where couples align
 * and where they differ on fundamental life values and priorities.
 *
 * Design Philosophy:
 * - Reveals alignment and divergence without judgement
 * - Distinguishes between negotiables and non-negotiables
 * - Creates space for values exploration and dialogue
 * - Recognises that some differences enrich relationships whilst others create fundamental incompatibility
 * - DDD brand voice: warm, research-grounded, insightful, authentic
 * - Australian English spelling throughout
 */

const ValuesAlignmentProfile = {
  id: 'VAP-1',
  title: "Values & Priorities Alignment",
  subtitle: "Explore where your values align and where they diverge, and why that matters",
  duration: "15-20 minutes",
  questionCount: 20, // 4 questions per value category

  introduction: {
    heading: "Understanding Your Value Compatibility",
    content: `Values shape everything: how you spend your time, where you invest your energy, what you prioritise, and what you're willing to sacrifice.

When couples share core values, navigating life together feels natural. When values diverge significantly, even small decisions become battlegrounds.

This assessment helps you understand where your values align and where they differ and, more importantly, whether those differences are negotiable or deal-breakers.

**How it works:**
- You'll explore your priorities across five key areas
- Answer based on what actually matters to you, not what you think should matter
- Your partner can complete this separately, then you compare results
- Results will show alignment, divergence, and potential areas of conflict

There's no 'right' way to value things. But there is value in understanding whether your values are compatible.

Ready to explore what truly matters to you?`,
  },

  valueCategories: {
    lifePriorities: {
      name: "Life Priorities",
      description: "What you prioritise in life: career vs. family vs. personal growth, financial security vs. experiences, stability vs. adventure",
      icon: "🎯",
      color: "blue",
      module: 7, // Links to Module 7: Growing Together
      dimensions: ['careerFamilyBalance', 'financialVsExperiences', 'stabilityVsAdventure'],
      questions: [0, 1, 2, 3]
    },

    familyChildren: {
      name: "Family & Children",
      description: "Your desires and philosophies around children, parenting, and extended family involvement",
      icon: "👨‍👩‍👧‍👦",
      color: "rose",
      module: 7, // Links to Module 7: Growing Together
      dimensions: ['childrenDesire', 'parentingPhilosophy', 'familyInvolvement'],
      questions: [4, 5, 6, 7]
    },

    lifestylePreferences: {
      name: "Lifestyle Preferences",
      description: "How you prefer to live: social vs. quiet, urban vs. rural, travel and adventure priorities",
      icon: "🏡",
      color: "green",
      module: 7, // Links to Module 7: Growing Together
      dimensions: ['socialVsQuiet', 'locationPreference', 'adventureOrientation'],
      questions: [8, 9, 10, 11]
    },

    relationshipPriorities: {
      name: "Relationship Priorities",
      description: "What you value most in relationships: independence vs. togetherness, romance vs. friendship, quality time vs. acts of service",
      icon: "💕",
      color: "purple",
      module: 5, // Links to Module 5: Intimacy & Vulnerability
      dimensions: ['autonomyVsConnection', 'relationshipFocus', 'loveLanguage'],
      questions: [12, 13, 14, 15]
    },

    personalValues: {
      name: "Personal Values",
      description: "Core values that guide your life: honesty, loyalty, growth, contribution, tradition, freedom",
      icon: "⭐",
      color: "amber",
      module: 7, // Links to Module 7: Growing Together
      dimensions: ['coreValues', 'traditionalVsProgressive', 'contributionOrientation'],
      questions: [16, 17, 18, 19]
    }
  },

  questions: [
    // LIFE PRIORITIES (Questions 0-3)
    {
      id: 0,
      category: 'lifePriorities',
      type: "ranking",
      text: "Rank these priorities in order of importance to you right now:",
      items: [
        { id: 'career', text: 'Career success and professional achievement' },
        { id: 'family', text: 'Family time and relationships' },
        { id: 'personal', text: 'Personal growth and self-development' },
        { id: 'financial', text: 'Financial security and wealth building' }
      ],
      weights: {
        careerFamilyBalance: { career: -2, family: 2, personal: 0, financial: 0 },
        financialVsExperiences: { career: 0, family: 0, personal: -1, financial: 2 },
        stabilityVsAdventure: { career: 1, family: 1, personal: -1, financial: 1 }
      }
    },

    {
      id: 1,
      category: 'lifePriorities',
      type: "scenario",
      text: "You receive a job offer with significantly higher pay but it requires relocating and working longer hours. You:",
      options: [
        {
          text: "Take it: career growth and financial security are top priorities",
          weights: {
            careerFamilyBalance: -2,
            financialVsExperiences: 2,
            stabilityVsAdventure: 1
          }
        },
        {
          text: "Decline: family stability and current relationships matter more",
          weights: {
            careerFamilyBalance: 2,
            financialVsExperiences: -1,
            stabilityVsAdventure: 2
          }
        },
        {
          text: "Discuss with partner: this decision affects both of us equally",
          weights: {
            careerFamilyBalance: 1,
            financialVsExperiences: 0,
            stabilityVsAdventure: 0
          }
        },
        {
          text: "Consider it carefully, weighing career, money, and lifestyle impact",
          weights: {
            careerFamilyBalance: 0,
            financialVsExperiences: 1,
            stabilityVsAdventure: 0
          }
        }
      ]
    },

    {
      id: 2,
      category: 'lifePriorities',
      type: "scenario",
      text: "If you had extra money to spend, you'd most likely:",
      options: [
        {
          text: "Save or invest it: financial security brings peace of mind",
          weights: {
            careerFamilyBalance: 0,
            financialVsExperiences: 2,
            stabilityVsAdventure: 2
          }
        },
        {
          text: "Spend it on experiences: travel, adventures, memories",
          weights: {
            careerFamilyBalance: 0,
            financialVsExperiences: -2,
            stabilityVsAdventure: -2
          }
        },
        {
          text: "Use it for home improvements or family needs",
          weights: {
            careerFamilyBalance: 1,
            financialVsExperiences: 0,
            stabilityVsAdventure: 1
          }
        },
        {
          text: "Invest in personal development: courses, coaching, growth",
          weights: {
            careerFamilyBalance: -1,
            financialVsExperiences: -1,
            stabilityVsAdventure: 0
          }
        }
      ]
    },

    {
      id: 3,
      category: 'lifePriorities',
      type: "scenario",
      text: "Your ideal life balance involves:",
      options: [
        {
          text: "Stable routines, predictable schedules, and comfortable familiarity",
          weights: {
            careerFamilyBalance: 0,
            financialVsExperiences: 1,
            stabilityVsAdventure: 2
          }
        },
        {
          text: "Adventure, spontaneity, and new experiences regularly",
          weights: {
            careerFamilyBalance: 0,
            financialVsExperiences: -1,
            stabilityVsAdventure: -2
          }
        },
        {
          text: "A mix: some stability with occasional adventure",
          weights: {
            careerFamilyBalance: 0,
            financialVsExperiences: 0,
            stabilityVsAdventure: 0
          }
        },
        {
          text: "Challenging yourself and growing, even if uncomfortable",
          weights: {
            careerFamilyBalance: -1,
            financialVsExperiences: -1,
            stabilityVsAdventure: -1
          }
        }
      ]
    },

    // FAMILY & CHILDREN (Questions 4-7)
    {
      id: 4,
      category: 'familyChildren',
      type: "scenario",
      text: "Regarding children in your relationship:",
      options: [
        {
          text: "I want children (or more children). It's a core life goal",
          weights: {
            childrenDesire: 2,
            parentingPhilosophy: 0,
            familyInvolvement: 1
          }
        },
        {
          text: "I'm content either way: open but not essential",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 0,
            familyInvolvement: 0
          }
        },
        {
          text: "I already have children; my family is complete",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 0,
            familyInvolvement: 0
          }
        },
        {
          text: "I don't want (or don't want more) children. I'm certain",
          weights: {
            childrenDesire: -2,
            parentingPhilosophy: 0,
            familyInvolvement: 0
          }
        }
      ]
    },

    {
      id: 5,
      category: 'familyChildren',
      type: "scenario",
      text: "Your parenting philosophy (actual or hypothetical) leans toward:",
      options: [
        {
          text: "Structured, with clear rules, boundaries, and expectations",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 2,
            familyInvolvement: 1
          }
        },
        {
          text: "Permissive, prioritising freedom and self-expression",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: -2,
            familyInvolvement: 0
          }
        },
        {
          text: "Balanced: clear boundaries with room for autonomy",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 0,
            familyInvolvement: 1
          }
        },
        {
          text: "Child-led, responsive to individual child's needs and temperament",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: -1,
            familyInvolvement: 0
          }
        }
      ]
    },

    {
      id: 6,
      category: 'familyChildren',
      type: "scenario",
      text: "Extended family involvement in your life:",
      options: [
        {
          text: "Is very important: regular contact and significant involvement",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 1,
            familyInvolvement: 2
          }
        },
        {
          text: "Is minimal: we prioritise our independence from family",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 0,
            familyInvolvement: -2
          }
        },
        {
          text: "Is balanced: regular contact but with clear boundaries",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 0,
            familyInvolvement: 1
          }
        },
        {
          text: "Is complicated: there's tension around family involvement",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 0,
            familyInvolvement: -1
          }
        }
      ]
    },

    {
      id: 7,
      category: 'familyChildren',
      type: "scenario",
      text: "If you have or were to have children, work-life balance would look like:",
      options: [
        {
          text: "One parent stays home or reduces work significantly",
          weights: {
            childrenDesire: 1,
            parentingPhilosophy: 2,
            familyInvolvement: 1
          }
        },
        {
          text: "Both parents continue careers with childcare support",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: -1,
            familyInvolvement: 0
          }
        },
        {
          text: "Flexible work arrangements to balance career and parenting",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 0,
            familyInvolvement: 1
          }
        },
        {
          text: "Significant family support (grandparents, etc.) in childcare",
          weights: {
            childrenDesire: 0,
            parentingPhilosophy: 0,
            familyInvolvement: 2
          }
        }
      ]
    },

    // LIFESTYLE PREFERENCES (Questions 8-11)
    {
      id: 8,
      category: 'lifestylePreferences',
      type: "scenario",
      text: "Your ideal social life involves:",
      options: [
        {
          text: "Frequent socialising: dinners, events, gatherings multiple times a week",
          weights: {
            socialVsQuiet: 2,
            locationPreference: 1,
            adventureOrientation: 1
          }
        },
        {
          text: "Occasional socialising: quality over quantity",
          weights: {
            socialVsQuiet: 0,
            locationPreference: 0,
            adventureOrientation: 0
          }
        },
        {
          text: "Minimal socialising: prefer quiet time at home",
          weights: {
            socialVsQuiet: -2,
            locationPreference: 0,
            adventureOrientation: -1
          }
        },
        {
          text: "Variable: sometimes social, sometimes reclusive",
          weights: {
            socialVsQuiet: 0,
            locationPreference: 0,
            adventureOrientation: 0
          }
        }
      ]
    },

    {
      id: 9,
      category: 'lifestylePreferences',
      type: "scenario",
      text: "Your ideal living situation is:",
      options: [
        {
          text: "Urban: city energy, walkability, cultural access",
          weights: {
            socialVsQuiet: 1,
            locationPreference: 2,
            adventureOrientation: 1
          }
        },
        {
          text: "Suburban: balance of space and access to amenities",
          weights: {
            socialVsQuiet: 0,
            locationPreference: 0,
            adventureOrientation: 0
          }
        },
        {
          text: "Rural: nature, space, quiet, slower pace",
          weights: {
            socialVsQuiet: -1,
            locationPreference: -2,
            adventureOrientation: 0
          }
        },
        {
          text: "Flexible: could be happy in various settings",
          weights: {
            socialVsQuiet: 0,
            locationPreference: 0,
            adventureOrientation: 1
          }
        }
      ]
    },

    {
      id: 10,
      category: 'lifestylePreferences',
      type: "scenario",
      text: "When it comes to travel and adventure:",
      options: [
        {
          text: "It's essential to my happiness; regular travel is a must",
          weights: {
            socialVsQuiet: 1,
            locationPreference: 0,
            adventureOrientation: 2
          }
        },
        {
          text: "I enjoy it occasionally but it's not a priority",
          weights: {
            socialVsQuiet: 0,
            locationPreference: 0,
            adventureOrientation: 0
          }
        },
        {
          text: "I prefer staying local. Travel is stressful more than enjoyable",
          weights: {
            socialVsQuiet: -1,
            locationPreference: 1,
            adventureOrientation: -2
          }
        },
        {
          text: "I'm open to it but financial constraints limit it",
          weights: {
            socialVsQuiet: 0,
            locationPreference: 0,
            adventureOrientation: 1
          }
        }
      ]
    },

    {
      id: 11,
      category: 'lifestylePreferences',
      type: "scenario",
      text: "Your preferred pace of life is:",
      options: [
        {
          text: "Fast-paced and stimulating; boredom is my enemy",
          weights: {
            socialVsQuiet: 2,
            locationPreference: 2,
            adventureOrientation: 2
          }
        },
        {
          text: "Moderate: balanced between activity and rest",
          weights: {
            socialVsQuiet: 0,
            locationPreference: 0,
            adventureOrientation: 0
          }
        },
        {
          text: "Slow and intentional: simplicity over stimulation",
          weights: {
            socialVsQuiet: -2,
            locationPreference: -1,
            adventureOrientation: -2
          }
        },
        {
          text: "Variable: intense periods followed by quiet recovery",
          weights: {
            socialVsQuiet: 0,
            locationPreference: 0,
            adventureOrientation: 1
          }
        }
      ]
    },

    // RELATIONSHIP PRIORITIES (Questions 12-15)
    {
      id: 12,
      category: 'relationshipPriorities',
      type: "scenario",
      text: "In relationships, what matters most to you?",
      options: [
        {
          text: "Deep emotional intimacy and vulnerability",
          weights: {
            autonomyVsConnection: -2,
            relationshipFocus: 1,
            loveLanguage: 0
          }
        },
        {
          text: "Companionship and friendship: enjoying life together",
          weights: {
            autonomyVsConnection: 0,
            relationshipFocus: -1,
            loveLanguage: 1
          }
        },
        {
          text: "Partnership and mutual support through life's challenges",
          weights: {
            autonomyVsConnection: 0,
            relationshipFocus: 0,
            loveLanguage: 2
          }
        },
        {
          text: "Maintaining individuality whilst being connected",
          weights: {
            autonomyVsConnection: 2,
            relationshipFocus: 0,
            loveLanguage: 0
          }
        }
      ]
    },

    {
      id: 13,
      category: 'relationshipPriorities',
      type: "scenario",
      text: "You feel most loved when your partner:",
      options: [
        {
          text: "Spends quality time with you: presence and attention",
          weights: {
            autonomyVsConnection: -1,
            relationshipFocus: 1,
            loveLanguage: 1
          }
        },
        {
          text: "Does helpful things: acts of service and practical support",
          weights: {
            autonomyVsConnection: 0,
            relationshipFocus: 0,
            loveLanguage: 2
          }
        },
        {
          text: "Gives physical affection: touch, closeness, intimacy",
          weights: {
            autonomyVsConnection: -2,
            relationshipFocus: 2,
            loveLanguage: 0
          }
        },
        {
          text: "Expresses appreciation verbally: words of affirmation",
          weights: {
            autonomyVsConnection: 0,
            relationshipFocus: 1,
            loveLanguage: 1
          }
        }
      ]
    },

    {
      id: 14,
      category: 'relationshipPriorities',
      type: "scenario",
      text: "The ideal relationship balance for you is:",
      options: [
        {
          text: "Deeply intertwined: we share everything and do most things together",
          weights: {
            autonomyVsConnection: -2,
            relationshipFocus: 2,
            loveLanguage: 0
          }
        },
        {
          text: "Independent but connected: separate lives that intersect regularly",
          weights: {
            autonomyVsConnection: 2,
            relationshipFocus: -1,
            loveLanguage: 1
          }
        },
        {
          text: "Interdependent: balancing 'we' and 'me' intentionally",
          weights: {
            autonomyVsConnection: 0,
            relationshipFocus: 0,
            loveLanguage: 1
          }
        },
        {
          text: "Partnership-focussed: a team working toward shared goals",
          weights: {
            autonomyVsConnection: 0,
            relationshipFocus: 0,
            loveLanguage: 2
          }
        }
      ]
    },

    {
      id: 15,
      category: 'relationshipPriorities',
      type: "scenario",
      text: "Romance and passion in relationships are:",
      options: [
        {
          text: "Essential. Without romance, it's just friendship",
          weights: {
            autonomyVsConnection: -1,
            relationshipFocus: 2,
            loveLanguage: 0
          }
        },
        {
          text: "Nice but not necessary; deep friendship matters more",
          weights: {
            autonomyVsConnection: 0,
            relationshipFocus: -2,
            loveLanguage: 1
          }
        },
        {
          text: "Important but evolves: different in long-term relationships",
          weights: {
            autonomyVsConnection: 0,
            relationshipFocus: 1,
            loveLanguage: 0
          }
        },
        {
          text: "Secondary to practical partnership and life-building",
          weights: {
            autonomyVsConnection: 1,
            relationshipFocus: -1,
            loveLanguage: 2
          }
        }
      ]
    },

    // PERSONAL VALUES (Questions 16-19)
    {
      id: 16,
      category: 'personalValues',
      type: "ranking",
      text: "Rank these personal values in order of importance to you:",
      items: [
        { id: 'honesty', text: 'Honesty and authenticity' },
        { id: 'loyalty', text: 'Loyalty and commitment' },
        { id: 'growth', text: 'Growth and learning' },
        { id: 'contribution', text: 'Contribution and service to others' }
      ],
      weights: {
        coreValues: { honesty: 2, loyalty: 1, growth: 1, contribution: 1 },
        traditionalVsProgressive: { honesty: 0, loyalty: 1, growth: -1, contribution: 0 },
        contributionOrientation: { honesty: 0, loyalty: 0, growth: 0, contribution: 2 }
      }
    },

    {
      id: 17,
      category: 'personalValues',
      type: "scenario",
      text: "When facing a difficult ethical decision, you prioritise:",
      options: [
        {
          text: "Absolute honesty, even if it causes short-term harm",
          weights: {
            coreValues: 2,
            traditionalVsProgressive: 0,
            contributionOrientation: 0
          }
        },
        {
          text: "Loyalty to people I care about, even if it means bending truth",
          weights: {
            coreValues: -1,
            traditionalVsProgressive: 1,
            contributionOrientation: 1
          }
        },
        {
          text: "The outcome that causes least harm overall",
          weights: {
            coreValues: 0,
            traditionalVsProgressive: -1,
            contributionOrientation: 2
          }
        },
        {
          text: "Following established rules and moral principles",
          weights: {
            coreValues: 1,
            traditionalVsProgressive: 2,
            contributionOrientation: 0
          }
        }
      ]
    },

    {
      id: 18,
      category: 'personalValues',
      type: "scenario",
      text: "Your approach to tradition and convention is:",
      options: [
        {
          text: "Traditional: I value established ways and cultural heritage",
          weights: {
            coreValues: 0,
            traditionalVsProgressive: 2,
            contributionOrientation: 0
          }
        },
        {
          text: "Progressive: I question tradition and embrace change",
          weights: {
            coreValues: 1,
            traditionalVsProgressive: -2,
            contributionOrientation: 0
          }
        },
        {
          text: "Balanced: respect some traditions, challenge others",
          weights: {
            coreValues: 0,
            traditionalVsProgressive: 0,
            contributionOrientation: 0
          }
        },
        {
          text: "Individualistic: I create my own path regardless of convention",
          weights: {
            coreValues: 1,
            traditionalVsProgressive: -1,
            contributionOrientation: -1
          }
        }
      ]
    },

    {
      id: 19,
      category: 'personalValues',
      type: "scenario",
      text: "Making a positive contribution to the world is:",
      options: [
        {
          text: "Central to my purpose: I need my life to make a difference",
          weights: {
            coreValues: 1,
            traditionalVsProgressive: -1,
            contributionOrientation: 2
          }
        },
        {
          text: "Important but balanced with personal and family priorities",
          weights: {
            coreValues: 0,
            traditionalVsProgressive: 0,
            contributionOrientation: 1
          }
        },
        {
          text: "Something I think about but don't actively pursue",
          weights: {
            coreValues: 0,
            traditionalVsProgressive: 0,
            contributionOrientation: -1
          }
        },
        {
          text: "Not a primary driver. I focus on my own life and loved ones",
          weights: {
            coreValues: 0,
            traditionalVsProgressive: 1,
            contributionOrientation: -2
          }
        }
      ]
    }
  ],

  // Calculate scores from responses
  calculateScores: (responses) => {
    const dimensionScores = {};
    const dimensionCounts = {};

    // Initialize all dimensions
    Object.values(ValuesAlignmentProfile.valueCategories).forEach(category => {
      category.dimensions.forEach(dim => {
        dimensionScores[dim] = 0;
        dimensionCounts[dim] = 0;
      });
    });

    // Aggregate weighted scores
    responses.forEach(response => {
      const question = ValuesAlignmentProfile.questions[response.questionId];

      if (question.type === 'ranking' && response.ranking) {
        // Handle ranking questions
        response.ranking.forEach((itemId, rank) => {
          const weight = question.weights;
          Object.entries(weight).forEach(([dimension, itemWeights]) => {
            if (itemWeights[itemId] !== undefined && dimensionScores[dimension] !== undefined) {
              // Weight based on rank (1st = full weight, 2nd = 75%, 3rd = 50%, 4th = 25%)
              const rankMultiplier = (4 - rank) / 4;
              dimensionScores[dimension] += itemWeights[itemId] * rankMultiplier;
              dimensionCounts[dimension]++;
            }
          });
        });
      } else if (question.type === 'scenario' && response.selectedIndex !== undefined) {
        // Handle scenario questions
        const option = question.options[response.selectedIndex];
        if (option.weights) {
          Object.entries(option.weights).forEach(([dimension, weight]) => {
            if (dimensionScores[dimension] !== undefined) {
              dimensionScores[dimension] += weight;
              dimensionCounts[dimension]++;
            }
          });
        }
      }
    });

    // Normalise to 0-100 scale
    const normalisedScores = {};
    Object.keys(dimensionScores).forEach(dim => {
      if (dimensionCounts[dim] > 0) {
        const avgScore = dimensionScores[dim] / dimensionCounts[dim];
        // Convert from -2 to +2 range to 0-100
        normalisedScores[dim] = Math.round(((avgScore + 2) / 4) * 100);
      } else {
        normalisedScores[dim] = 50;
      }
    });

    return normalisedScores;
  },

  // Compare partner scores to determine alignment
  comparePartnerScores: (yourScores, partnerScores) => {
    const alignmentScores = {};

    Object.keys(yourScores).forEach(dimension => {
      const difference = Math.abs(yourScores[dimension] - partnerScores[dimension]);
      // Convert difference to alignment score (0-100, where 100 is perfect alignment)
      alignmentScores[dimension] = Math.round(100 - difference);
    });

    return alignmentScores;
  },

  // Determine overall alignment type.
  // `alignmentScores` come from comparePartnerScores (100 = same answers).
  // Alignment needs two people: called with one person's own scores (no
  // partner scores), this returns the individual profile instead.
  getAlignmentType: (alignmentScores, partnerOneScores, partnerTwoScores) => {
    if (!partnerOneScores || !partnerTwoScores) {
      return 'individualProfile';
    }

    // Opposite answers on children: one partner leans clearly towards
    // having children and the other clearly away
    const p1Children = partnerOneScores.childrenDesire ?? 50;
    const p2Children = partnerTwoScores.childrenDesire ?? 50;
    if ((p1Children > 70 && p2Children < 30) || (p1Children < 30 && p2Children > 70)) {
      return 'criticalMisalignment';
    }

    // Calculate average alignment
    const avgScore = Object.values(alignmentScores).reduce((a, b) => a + b, 0) / Object.keys(alignmentScores).length;

    if (avgScore >= 75) return 'strongAlignment';
    if (avgScore >= 60) return 'goodAlignment';
    if (avgScore >= 45) return 'moderateAlignment';
    if (avgScore >= 30) return 'significantDivergence';
    return 'fundamentalIncompatibility';
  },

  alignmentTypes: {
    individualProfile: {
      name: "Your Values Profile",
      tagline: "What matters most to you across five life areas",
      description: "This profile shows your own values and priorities. Alignment is about two people, so it can't be measured from one set of answers. To see where you and your partner align and where you differ, take the assessment together using Share with Partner. Until then, use your scores below as a starting point for a conversation.",
      strengths: [
        "A clearer picture of what matters most to you",
        "Language for describing your priorities to your partner",
        "A starting point for a values conversation"
      ],
      considerations: [
        "Notice which areas feel non-negotiable and which feel flexible",
        "Ask your partner how they'd answer the same questions",
        "Talk about differences with curiosity rather than to win",
        "Values can shift over time, so revisit this together"
      ]
    },

    strongAlignment: {
      name: "Strong Values Alignment",
      tagline: "Your core values and priorities align beautifully",
      description: "You and your partner share remarkably similar values across most dimensions. You want similar things from life, prioritise the same areas, and hold compatible beliefs about what matters. This alignment makes navigating life together natural and intuitive. Small differences can actually enrich your relationship.",
      strengths: [
        "Shared vision for life direction",
        "Natural agreement on priorities",
        "Compatible lifestyle preferences",
        "Aligned relationship values"
      ],
      considerations: [
        "Don't take alignment for granted; continue discussing values",
        "Small differences can still cause friction if not acknowledged",
        "Values can shift over time, so check in regularly",
        "Celebrate this foundation; it's a significant strength"
      ]
    },

    goodAlignment: {
      name: "Good Values Alignment",
      tagline: "Your values align in key areas with navigable differences",
      description: "You share core values in most important areas whilst differing in some specifics. These differences are unlikely to derail your relationship if you navigate them with awareness and respect. Focus on understanding why you differ and finding ways to honour both perspectives.",
      strengths: [
        "Alignment on fundamental values",
        "Differences are negotiable and enriching",
        "Mutual respect for differing perspectives",
        "Flexibility in adapting to each other"
      ],
      considerations: [
        "Identify which differences need active negotiation",
        "Understand the 'why' behind differing values",
        "Create space for both perspectives to coexist",
        "Avoid assuming alignment where it doesn't exist"
      ]
    },

    moderateAlignment: {
      name: "Moderate Values Alignment",
      tagline: "Significant differences exist: intentional navigation required",
      description: "You differ significantly in several value areas. These differences aren't necessarily deal-breakers, but they require conscious effort, compromise, and ongoing dialogue. Without intentional navigation, these divergences can create recurring conflict and resentment.",
      strengths: [
        "Awareness of where you differ",
        "Opportunity for growth through difference",
        "Potential for complementary perspectives",
        "Motivation to understand each other deeply"
      ],
      considerations: [
        "Identify non-negotiables vs. areas for compromise",
        "Discuss how you'll navigate specific differences",
        "Seek to understand, not just to be understood",
        "Consider whether differences are sustainable long-term"
      ]
    },

    significantDivergence: {
      name: "Significant Value Divergence",
      tagline: "Major differences require serious conversation",
      description: "You diverge significantly on multiple fundamental values. These aren't surface-level differences. They reflect core beliefs about how life should be lived. Whilst some couples navigate this successfully, it requires exceptional communication, respect, and willingness to compromise. Honest conversation about whether your values are compatible long-term is essential.",
      strengths: [
        "Clarity about where you differ",
        "Opportunity for radical honesty",
        "Growth through navigating difference",
        "Potential to learn from opposing viewpoints"
      ],
      considerations: [
        "Have explicit conversations about deal-breakers",
        "Assess whether compromises feel sustainable",
        "Consider whether differences will magnify over time",
        "Seek professional support to navigate divergence"
      ]
    },

    fundamentalIncompatibility: {
      name: "Fundamental Value Incompatibility",
      tagline: "Core values differ significantly. Compatibility requires serious assessment",
      description: "Your values diverge fundamentally across multiple critical areas. Whilst love can bridge many differences, significant value incompatibility often leads to chronic dissatisfaction, resentment, and relationship erosion. This doesn't mean you're bad for each other. It means you want fundamentally different things from life. Honest assessment of long-term compatibility is necessary.",
      strengths: [
        "Clarity about fundamental differences",
        "Opportunity for radical honesty before deeper commitment",
        "Awareness that love alone isn't always enough",
        "Courage to face difficult truths"
      ],
      considerations: [
        "Consider whether these differences are sustainable",
        "Assess whether compromises would breed resentment",
        "Explore whether counselling can help navigate incompatibility",
        "Face the question: can we build a life together given these differences?"
      ]
    },

    criticalMisalignment: {
      name: "Critical Misalignment (Children)",
      tagline: "Fundamental disagreement on children requires immediate attention",
      description: "You differ fundamentally on whether to have children. One wants them, the other doesn't. This is among the most significant incompatibilities a couple can face because there's no middle ground: you can't have half a child. If this difference is real and unchanging, one person will sacrifice a core life desire, which often breeds profound resentment. This requires immediate, honest dialogue.",
      strengths: [
        "Clarity on a critical incompatibility",
        "Awareness before it's too late to make other choices",
        "Opportunity for radical honesty",
        "Courage to face difficult realities"
      ],
      considerations: [
        "This is not a difference you can compromise on",
        "Explore whether either position might genuinely shift",
        "Consider what sacrifice would mean for each person",
        "Seek professional support to navigate this conversation",
        "Face the reality: is this relationship sustainable?"
      ]
    }
  },

  completionMessage: {
    heading: "Your Values Alignment Profile is Ready",
    content: `We've explored your values and priorities across five critical life areas.

Values aren't right or wrong; they're simply what matters to you. But value compatibility profoundly affects relationship satisfaction and longevity.

Where you align, life flows naturally. Where you diverge, intentional navigation is required. Some differences enrich relationships. Others erode them over time.

The question isn't whether you differ. It's whether your differences are negotiable or deal-breakers.

Let's see what your values reveal...`
  }
};

export default ValuesAlignmentProfile;
